import { makeAutoObservable } from "mobx";
import { ORG_SUBSCRIPTION_PLANS } from "../../constants/billing.ts";
import { SESSION_STORAGE_KEYS, ssClearUsagePopupState, ssGet, ssSet } from "../../helpers/sessionStorage.ts";
import { IRootStore } from "../../mobx/root-store.tsx";

export interface IFiboUsageStore {
	isOrgLessUser: boolean;
	isFreeOrgUser: boolean;
	isStarterOrgUser: boolean;
	isStarterOrgAndDiscounted: boolean;
	isEligibleForUsagePopup(): boolean;
	usageClickThreshold: number;
	usageClicksThisSession: number;
	usagePopupShownThisSession: boolean;
	exitIntentPopupShownThisSession: boolean;
	resetUsageEngagementPopupState(): void;
	registerGenerationRefineClick(): void;
	markUsagePopupShown(): void;
	markExitIntentPopupShown(): void;
	canShowUsagePopupThisSession(): boolean;
	canShowExitIntentPopupThisSession(): boolean;
}

export default class FiboUsageStore implements IFiboUsageStore {
	rootStore: IRootStore;
	usageClickThreshold = 10;
	usageClicksThisSession = 0;
	usagePopupShownThisSession = false;
	exitIntentPopupShownThisSession = false;

	get isOrgLessUser(): boolean {
		return (this.rootStore.authStore.userOrganizations?.length ?? 0) === 0;
	}

	get isFreeOrgUser(): boolean {
		return (
			this.rootStore.authStore.orgSubscription?.plan_name?.toLowerCase?.() ===
			ORG_SUBSCRIPTION_PLANS.free.name.toLowerCase()
		);
	}

	get isStarterOrgUser(): boolean {
		return (
			this.rootStore.authStore.orgSubscription?.plan_name?.toLowerCase?.() ===
				ORG_SUBSCRIPTION_PLANS.starter.name.toLowerCase() && !this.rootStore.authStore.orgSubscription?.discount
		);
	}

	get isStarterOrgAndDiscounted(): boolean {
		return (
			this.rootStore.authStore.orgSubscription?.plan_name?.toLowerCase?.() ===
				ORG_SUBSCRIPTION_PLANS.starter.name.toLowerCase() &&
			Boolean(this.rootStore.authStore.orgSubscription?.discount)
		);
	}

	isEligibleForUsagePopup = (): boolean => {
		return this.isOrgLessUser || this.isFreeOrgUser || this.isStarterOrgUser || this.isStarterOrgAndDiscounted;
	};

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;

		const clicks = Number(ssGet(SESSION_STORAGE_KEYS.usageClicks) || "0");
		this.usageClicksThisSession = Number.isFinite(clicks) ? clicks : 0;
		this.usagePopupShownThisSession = ssGet(SESSION_STORAGE_KEYS.usageShown) === "true";
		this.exitIntentPopupShownThisSession = ssGet(SESSION_STORAGE_KEYS.exitShown) === "true";
	}

	resetUsageEngagementPopupState = (): void => {
		// Remove only the keys we set for the popup/session logic
		sessionStorage.removeItem("usageClicksThisSession");
		sessionStorage.removeItem("usagePopupShownThisSession");
		sessionStorage.removeItem("exitIntentPopupShownThisSession");
		this.usageClicksThisSession = 0;
		this.usagePopupShownThisSession = false;
		this.exitIntentPopupShownThisSession = false;

		// sessionStorage
		ssClearUsagePopupState();
	};

	// Register how many times the user has clicked the Generate/Refine button
	registerGenerationRefineClick = (): void => {
		this.usageClicksThisSession += 1;
		ssSet(SESSION_STORAGE_KEYS.usageClicks, String(this.usageClicksThisSession));
	};

	// Mark that the usage popup has been shown
	markUsagePopupShown = (): void => {
		this.usagePopupShownThisSession = true;
		ssSet(SESSION_STORAGE_KEYS.usageShown, "true");
	};

	// Mark that the exit intent popup has been shown
	markExitIntentPopupShown = (): void => {
		this.exitIntentPopupShownThisSession = true;
		ssSet(SESSION_STORAGE_KEYS.exitShown, "true");
	};

	canShowUsagePopupThisSession = (): boolean => {
		const shown = ssGet(SESSION_STORAGE_KEYS.usageShown) === "true";
		const clicks = Number(ssGet(SESSION_STORAGE_KEYS.usageClicks) || "0");
		return this.isEligibleForUsagePopup() && !shown && clicks === this.usageClickThreshold;
	};

	canShowExitIntentPopupThisSession = (): boolean => {
		const shown = ssGet(SESSION_STORAGE_KEYS.exitShown) === "true";
		return this.isEligibleForUsagePopup() && !shown;
	};
}
